import React from 'react';
import { useApolloClient, useQuery } from '@apollo/client';

import { GradientProgressBar, IconMusicFile } from '@trqk/components';
import {
    getAudioUploadsLocalStorageKey,
    locale,
    setInLocalStorage,
} from '@trqk/utils';
import { queryStrings } from '@trqk/resolvers';

import { contentWidth, progressBarSpacer, rowHeight } from '../../../constants';
import { UploadAudioError } from '../../../../../toastMessages/uploadAudio';

import { useToast } from '../../../../../../hooks';

/**
 * @typedef {UploadAudioFailureRowProps}
 * @property {UploadAudioFileOutput} uploadResult an uploadResult with .success === false
 */
interface UploadAudioFailureRowProps {
    uploadResult: UploadAudioFileOutput;
}

const { GET_SESSION_CURRENT_USER } = queryStrings;
const height = rowHeight - 2 * progressBarSpacer;

/**
 * Renders a GradientProgressBar in an error state.
 * @param {FailureRowProps} props
 */
export const AudioUploadFailureRow = ({
    uploadResult,
}: UploadAudioFailureRowProps): JSX.Element | null => {
    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const userId = currentUserQuery?.data?.session?.currentUser?.id;
    const toast = useToast();
    const { id, filename, error } = uploadResult;
    const label = filename ?? '';
    const errorLabel = error ? locale.enUS.UPLOAD_AUDIO.UPLOAD_ERROR : null;
    const { cache } = useApolloClient();
    const localStorageKey = getAudioUploadsLocalStorageKey(userId);

    return (
        <div
            style={{
                marginTop: progressBarSpacer,
                marginBottom: progressBarSpacer,
            }}
        >
            <GradientProgressBar
                canceled={false}
                color="aqua"
                error={errorLabel}
                errorStyle="quiet"
                handleCancel={(): void => {
                    setInLocalStorage(
                        localStorageKey,
                        (
                            prev: UploadAudioFileOutput[] | null,
                        ): UploadAudioFileOutput[] | null => {
                            if (prev === null) {
                                return null;
                            }

                            return prev.filter(
                                (
                                    currentResult: UploadAudioFileOutput,
                                ): boolean => {
                                    if (currentResult.id === id) {
                                        return false;
                                    }

                                    return true;
                                },
                            );
                        },
                    );

                    cache.evict({
                        fieldName: 'audioUploads',
                    });
                }}
                handleExpandError={(): void => {
                    toast.add({
                        kind: 'warning-card',
                        RenderContent: UploadAudioError,
                        renderContentInput: {
                            errorDetails: uploadResult,
                        },
                        persist: true,
                    });
                }}
                height={height}
                Icon={IconMusicFile}
                inProgressLabel=""
                label={label}
                labelLines={2}
                progress={0}
                width={contentWidth}
            />
        </div>
    );
};
